import { Feature, View } from "ol";
import { Coordinate } from "ol/coordinate";
import { getCenter, getHeight, getWidth, isEmpty } from "ol/extent";
import { Geometry, MultiPolygon, Point, Polygon } from "ol/geom";
import VectorLayer from "ol/layer/Vector.js";
import { fromUserExtent } from "ol/proj";
import VectorSource from "ol/source/Vector.js";
import Icon from "ol/style/Icon";
import Style from "ol/style/Style";

import { createPinCanvas, PIN_PIXEL_RATIO } from "../../utils";
import { DEFAULT_PROJECT_COLOUR } from "./CommonUtils";

type BoundaryLayer = VectorLayer<VectorSource<Feature<Geometry>>, Feature<Geometry>>;

const PIN_HIDE_ZOOM_BY_SPAN: { minSpanViewMetres: number; hiddenFromZoom: number }[] = [
  { minSpanViewMetres: 50000, hiddenFromZoom: 8 },
  { minSpanViewMetres: 10000, hiddenFromZoom: 10 },
  { minSpanViewMetres: 2000, hiddenFromZoom: 12 },
  { minSpanViewMetres: 500, hiddenFromZoom: 14 },
  { minSpanViewMetres: 0, hiddenFromZoom: 15 },
];

const hidePinBelowResolution = (source: VectorSource<Feature<Geometry>>, view: View): number => {
  const extent = source.getExtent();
  const viewExtent = isEmpty(extent) ? undefined : fromUserExtent(extent, view.getProjection());
  const spanViewMetres = viewExtent ? Math.max(getWidth(viewExtent), getHeight(viewExtent)) : 0;

  /* The 0-metre entry always matches, so this only falls back for a NaN span. */
  const bucket = PIN_HIDE_ZOOM_BY_SPAN.find(({ minSpanViewMetres }) => spanViewMetres >= minSpanViewMetres);

  return view.getResolutionForZoom(bucket?.hiddenFromZoom ?? PIN_HIDE_ZOOM_BY_SPAN[0].hiddenFromZoom);
};

/* A MultiPolygon is one project drawn as several disjoint parcels, so its parts compete with the
   other features for the largest-parcel pick rather than being treated as one shape. */
const toPolygons = (features: Feature<Geometry>[]): Polygon[] =>
  features.flatMap((feature) => {
    const geometry = feature.getGeometry();
    if (geometry instanceof Polygon) return [geometry];
    if (geometry instanceof MultiPolygon) return geometry.getPolygons();

    return [];
  });

/**
 * Where a project's pin belongs: the interior middle of its largest parcel.
 *
 * `getInteriorPoint` is the midpoint of the polygon's widest horizontal slice, so it always lands
 * inside the shape — the bounding-box centre does not, falling in the notch of an L-shaped field or
 * the hole of a ring. Its coordinates are XYM, the third value being the length of that slice, so
 * only the first two are the position.
 */
const pinCoordinateFor = (source: VectorSource<Feature<Geometry>>): Coordinate | undefined => {
  const polygons = toPolygons(source.getFeatures());

  const largestPolygon = polygons.reduce<Polygon | undefined>(
    (largest, polygon) => (!largest || polygon.getArea() > largest.getArea() ? polygon : largest),
    undefined
  );

  const coordinate = largestPolygon?.getInteriorPoint().getCoordinates().slice(0, 2);

  if (coordinate) {
    /* A degenerate ring yields NaN, which OpenLayers would draw as a pin pegged to the map origin. */
    return coordinate.every((value) => Number.isFinite(value)) ? coordinate : undefined;
  }

  /* Not polygonal at all (a point or line boundary) — the extent centre is the best available. */
  const extent = source.getExtent();
  return isEmpty(extent) ? undefined : getCenter(extent);
};

const createPinFeature = (
  coordinate: Coordinate,
  fillColour: string,
  hideBelowResolution: number
): Feature<Geometry> | undefined => {
  const pinCanvas = createPinCanvas(fillColour);
  /* No 2d context (jsdom, or a browser refusing the canvas) — a pin with no artwork is worse than
     none, and the boundary polygon still marks the project. */
  if (!pinCanvas) return undefined;

  const pinFeature = new Feature<Geometry>({ geometry: new Point(coordinate) });

  const pinStyle = new Style({
    image: new Icon({
      img: pinCanvas,
      /* Bottom-centre, so the teardrop tip sits on the coordinate rather than its middle */
      anchor: [0.5, 1],
      /* The canvas is rasterised at 2x for retina; scale back to the logical box size */
      scale: 1 / PIN_PIXEL_RATIO,
    }),
  });

  /* A style function rather than a plain style, because each pin hides at a zoom of its own and
     OpenLayers only offers min/maxZoom per layer. Resolution falls as zoom rises, so returning
     nothing once it drops past the threshold is what hides the pin from that zoom in. */
  pinFeature.setStyle((_feature, resolution) => (resolution > hideBelowResolution ? pinStyle : undefined));

  return pinFeature;
};

/**
 * One location pin per drawn boundary, positioned from that boundary's own geometry and tinted to its
 * fill colour.
 *
 * Taking the boundary layers rather than the source data is what keeps a pin from ever marking a
 * project the map isn't showing: there is no path to a pin that does not go through a drawn boundary.
 * Project location coordinates are deliberately not consulted — they exist independently of whether
 * a boundary was ever drawn.
 *
 * A single layer holds every pin: both the colour and the zoom cutoff live on each feature's own
 * style, so the layer itself constrains nothing.
 *
 * The layer deliberately carries no `projectName` property. The location tab's hover tooltip walks
 * `forEachFeatureAtPixel` looking for that property, so leaving it off lets the search fall through
 * a pin to the boundary underneath — the pin stays a pure locator.
 *
 * Takes the view because a pin's cutoff depends on its boundary's size in view units, and returns a
 * disposer: the boundary sources outlive this layer, so a pin layer built for a map that has since
 * been torn down must stop listening to them.
 */
export const createProjectPinLayer = (
  boundaryLayers: BoundaryLayer[],
  view: View
): { pinLayer: BoundaryLayer; dispose: () => void } => {
  const pinSource = new VectorSource<Feature<Geometry>>();
  const disposers: (() => void)[] = [];

  boundaryLayers.forEach((boundaryLayer) => {
    const source = boundaryLayer.getSource();
    if (!source) return;

    const fillColour: string = boundaryLayer.get("fillColor") || DEFAULT_PROJECT_COLOUR;

    /* A layer can be given inline features *and* a url, so featuresloadend may fire after a pin has
       already been placed. One pin per boundary, whichever path its geometry arrived by. */
    let placed = false;
    const placePin = (): void => {
      if (placed) return;

      const coordinate = pinCoordinateFor(source);
      if (!coordinate) return;

      const pinFeature = createPinFeature(coordinate, fillColour, hidePinBelowResolution(source, view));
      if (!pinFeature) return;

      pinSource.addFeature(pinFeature);
      placed = true;
    };

    placePin();

    /* Boundaries without inline content load over the network, so they have no geometry yet. */
    if (!placed) {
      source.once("featuresloadend", placePin);
      disposers.push(() => source.un("featuresloadend", placePin));
    }
  });

  return {
    pinLayer: new VectorLayer({ source: pinSource }),
    dispose: () => disposers.forEach((disposeListener) => disposeListener()),
  };
};
